import { useEffect } from 'react'

export type SyncScrollDirection = 'vertical' | 'horizontal' | 'both'

export type SyncScrollTarget = {
  element: HTMLElement
  direction: SyncScrollDirection
}

const hasX = (d: SyncScrollDirection) => d === 'horizontal' || d === 'both'
const hasY = (d: SyncScrollDirection) => d === 'vertical' || d === 'both'

export function useSyncScroll(targets: SyncScrollTarget[]) {
  useEffect(() => {
    if (!targets || targets.length < 2) return

    const list: SyncScrollTarget[] = []
    const seen = new Set<HTMLElement>()

    for (const t of targets) {
      if (!t?.element) {
        continue
      }

      if (seen.has(t.element)) {
        continue
      }

      seen.add(t.element)
      list.push(t)
    }

    if (list.length < 2) {
      return
    }

    const ignore = new WeakMap<HTMLElement, number>()

    let rafId = 0
    let pendingSource: HTMLElement | null = null
    let pendingLeft = 0
    let pendingTop = 0

    const markIgnore = (el: HTMLElement) => {
      ignore.set(el, (ignore.get(el) ?? 0) + 1)
    }

    const shouldIgnore = (el: HTMLElement) => {
      const n = ignore.get(el) ?? 0
      if (n <= 0) return false
      if (n === 1) ignore.delete(el)
      else ignore.set(el, n - 1)
      return true
    }

    const flush = () => {
      rafId = 0
      const source = pendingSource
      if (!source) return

      const sourceItem = list.find((t) => t.element === source)
      if (!sourceItem) return

      const srcX = hasX(sourceItem.direction)
      const srcY = hasY(sourceItem.direction)

      for (const t of list) {
        if (t.element === source) continue

        const canX = srcX && hasX(t.direction)
        const canY = srcY && hasY(t.direction)

        if (canX) {
          const nextLeft = pendingLeft
          if (t.element.scrollLeft !== nextLeft) {
            markIgnore(t.element)
            t.element.scrollLeft = nextLeft
          }
        }

        if (canY) {
          const nextTop = pendingTop
          if (t.element.scrollTop !== nextTop) {
            markIgnore(t.element)
            t.element.scrollTop = nextTop
          }
        }
      }
    }

    const schedule = (source: HTMLElement) => {
      pendingSource = source
      pendingLeft = source.scrollLeft
      pendingTop = source.scrollTop

      if (rafId) return
      rafId = requestAnimationFrame(flush)
    }

    const listeners: Array<{
      el: HTMLElement
      fn: () => void
    }> = []

    for (const t of list) {
      const el = t.element
      const onScroll = () => {
        if (shouldIgnore(el)) return
        schedule(el)
      }
      listeners.push({ el, fn: onScroll })
      el.addEventListener('scroll', onScroll, { passive: true })
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      for (const { el, fn } of listeners) {
        el.removeEventListener('scroll', fn as EventListener)
      }
    }
  }, [targets])
}
